import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Play, Pause, Volume2, VolumeX, Rewind, FastForward } from "lucide-react";
import EqIndicator from "./EqIndicator";
import CoverArt from "./CoverArt";
import useMediaSession from "@/hooks/useMediaSession";

function fmt(t) {
  if (!isFinite(t) || t < 0) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const AudioPlayer = forwardRef(function AudioPlayer(
  { src, title, artist, onTimeUpdate, autoPlay = false, seekRef, onPlayingChange, onAudioElement, song = null },
  ref
) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const [muted, setMuted] = useState(false);
  const [hoverPct, setHoverPct] = useState(null);

  // Expose the underlying <audio> element to the parent (visualizer, etc.)
  useEffect(() => {
    onAudioElement?.(audioRef.current);
    return () => onAudioElement?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => audioRef.current, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!seekRef) return;
    seekRef.current = (t) => {
      const a = audioRef.current;
      if (!a) return;
      a.currentTime = Math.max(0, t);
      setCurrent(a.currentTime);
      if (a.paused) a.play().catch(() => {});
    };
    return () => {
      if (seekRef) seekRef.current = null;
    };
  }, [seekRef]);

  useEffect(() => {
    onPlayingChange?.(playing);
  }, [playing, onPlayingChange]);

  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    const a = audioRef.current;
    if (a && autoPlay) {
      const tryPlay = () => {
        a.play()
          .then(() => setPlaying(true))
          .catch(() => {});
      };
      a.addEventListener("loadedmetadata", tryPlay, { once: true });
      return () => a.removeEventListener("loadedmetadata", tryPlay);
    }
  }, [src, autoPlay]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      try {
        await a.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const onSeek = (val) => {
    const a = audioRef.current;
    if (!a) return;
    const t = (val[0] / 100) * (duration || 0);
    a.currentTime = t;
    setCurrent(t);
  };

  const onVol = (val) => {
    const v = val[0] / 100;
    setVolume(v);
    setMuted(v === 0);
  };

  const skip = (sec) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration || 0, a.currentTime + sec));
  };

  useMediaSession(audioRef, { song, title, artist, playing, duration, skip });

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(94vw,1100px)] px-2"
      data-testid="audio-player-dock"
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime || 0;
          setCurrent(t);
          onTimeUpdate?.(t);
        }}
        onEnded={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        crossOrigin="anonymous"
      />
      <div
        className="relative rounded-2xl bg-[#0E0E0E]/85 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] px-3 sm:px-5 py-3"
        data-testid="audio-player-bar"
      >
        {/* Edge progress ribbon */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/5 overflow-hidden rounded-t-2xl">
          <div
            className="h-full bg-[#FF3B30]"
            style={{ width: `${pct}%`, transition: "width 0.15s linear" }}
          />
        </div>

        <div className="grid grid-cols-12 gap-3 sm:gap-5 items-center">
          {/* meta */}
          <div className="col-span-12 sm:col-span-3 flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 shrink-0 relative">
              <CoverArt
                song={song || { id: undefined, title, has_cover: false }}
                className="w-full h-full"
                rounded="rounded-md"
                initialsClassName="text-2xl"
              />
              {playing && (
                <span className="absolute inset-0 ring-1 ring-[#FF3B30]/40 rounded-md animate-pulse pointer-events-none" />
              )}
            </div>
            <div className="min-w-0">
              <div
                className="font-display font-bold text-sm truncate flex items-center gap-2"
                data-testid="player-title"
              >
                <span className="truncate">{title || "—"}</span>
                <EqIndicator playing={playing} className="shrink-0" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 truncate">
                {artist || "unknown artist"}
              </div>
            </div>
          </div>

          {/* controls */}
          <div className="col-span-12 sm:col-span-6 flex flex-col items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => skip(-10)}
                className="text-white/60 hover:text-white hover:bg-white/5 h-9 w-9 rounded-full"
                data-testid="player-skip-back-btn"
                title="Rewind 10s"
              >
                <Rewind className="h-4 w-4" />
              </Button>
              <Button
                onClick={toggle}
                className="h-12 w-12 rounded-full bg-white text-black hover:bg-white p-0 relative overflow-hidden group"
                data-testid="player-play-pause-btn"
                title={playing ? "Pause" : "Play"}
              >
                <span
                  className="absolute inset-0 rounded-full bg-[#FF3B30]/0 group-hover:bg-[#FF3B30]/10 transition-colors"
                  aria-hidden
                />
                {playing ? (
                  <Pause className="h-5 w-5 fill-current relative" />
                ) : (
                  <Play className="h-5 w-5 fill-current ml-0.5 relative" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => skip(10)}
                className="text-white/60 hover:text-white hover:bg-white/5 h-9 w-9 rounded-full"
                data-testid="player-skip-fwd-btn"
                title="Forward 10s"
              >
                <FastForward className="h-4 w-4" />
              </Button>
            </div>
            <div className="w-full flex items-center gap-3">
              <span
                className="font-mono text-[11px] text-white/50 w-10 text-right tabular-nums"
                data-testid="player-current-time"
              >
                {fmt(current)}
              </span>
              <div
                className="relative flex-1"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const p = Math.max(
                    0,
                    Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
                  );
                  setHoverPct(p);
                }}
                onMouseLeave={() => setHoverPct(null)}
              >
                <Slider
                  value={[pct]}
                  max={100}
                  step={0.1}
                  onValueChange={onSeek}
                  className="lyriq-slider"
                  data-testid="player-seek-slider"
                />
                {hoverPct !== null && duration > 0 && (
                  <div
                    className="absolute -top-7 px-1.5 py-0.5 rounded-sm bg-[#FF3B30] text-white font-mono text-[10px] -translate-x-1/2 pointer-events-none whitespace-nowrap"
                    style={{ left: `${hoverPct}%` }}
                  >
                    {fmt((hoverPct / 100) * duration)}
                  </div>
                )}
              </div>
              <span
                className="font-mono text-[11px] text-white/50 w-10 tabular-nums"
                data-testid="player-duration"
              >
                {fmt(duration)}
              </span>
            </div>
          </div>

          {/* volume */}
          <div className="hidden sm:flex col-span-3 items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setMuted((m) => {
                  const next = !m;
                  if (audioRef.current) audioRef.current.muted = next;
                  return next;
                });
              }}
              className="text-white/60 hover:text-white hover:bg-white/5 rounded-full"
              data-testid="player-mute-btn"
              title={muted || volume === 0 ? "Unmute" : "Mute"}
            >
              {muted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[Math.round(volume * 100)]}
              max={100}
              onValueChange={onVol}
              className="lyriq-slider w-28"
              data-testid="player-volume-slider"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default AudioPlayer;
